import { useDroppable } from '@dnd-kit/core';
import { Folder, FolderOpen, MoreVertical, FolderPlus, Edit2, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface DroppableTeamFolderItemProps {
  id: string;
  name: string;
  teamId: string;
  workflowCount?: number;
  onClick: () => void;
  onAddFolder?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
}

export function DroppableTeamFolderItem({
  id,
  name,
  teamId,
  workflowCount,
  onClick,
  onAddFolder,
  onRename,
  onDelete,
}: DroppableTeamFolderItemProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: `team-folder-${id}`,
    data: {
      type: 'team-folder',
      teamFolderId: id,
      teamId: teamId,
    },
  });

  return (
    <div className="relative">
      <div
        ref={setNodeRef}
        className={cn(
          'transition-all duration-200',
          isOver && 'bg-primary/10 ring-2 ring-primary rounded-md'
        )}
      >
        <div className="group relative flex items-center -ml-4">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'w-full justify-start h-6 text-[10px] ml-4 pr-8 min-w-0',
                  isOver && 'bg-primary/5'
                )}
                onClick={onClick}
              >
                {isOver ? (
                  <FolderOpen className="h-3 w-3 mr-2 text-primary flex-shrink-0" />
                ) : (
                  <Folder className="h-3 w-3 mr-2 flex-shrink-0" />
                )}
                <span className="flex-1 text-left truncate min-w-0 max-w-[100px]">{name}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{name}</p>
            </TooltipContent>
          </Tooltip>
          {/* Count and menu icon in same position */}
          {workflowCount !== undefined && workflowCount > 0 && (
            <span className="absolute right-3 text-[10px] text-muted-foreground group-hover:hidden w-5 text-center">
              {workflowCount}
            </span>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-3 h-5 w-5 bg-background opacity-0 group-hover:opacity-100"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onAddFolder && (
                <DropdownMenuItem onClick={onAddFolder}>
                  <FolderPlus className="mr-2 h-4 w-4" />
                  Add Folder
                </DropdownMenuItem>
              )}
              {onRename && (
                <DropdownMenuItem onClick={onRename}>
                  <Edit2 className="mr-2 h-4 w-4" />
                  Rename
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem onClick={onDelete} className="text-destructive">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {isOver && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 border-2 border-primary border-dashed rounded-md animate-pulse" />
        </div>
      )}
    </div>
  );
}
